import {
  BoardCertification,
  BoardCertificationStatuses,
  CreatedBoardCertification,
} from '~/types/mcv/boardCertifications';
import { ACTIVE_STATUSES, INACTIVE_STATUSES } from './constants';
import { BoardCertFormValues } from './types';

/**
 * Serializes the board-certification form into the API's create/update payload.
 *
 * Certificate identity is read only from the pair belonging to the selected
 * `certType`, and every name field accepts either the chosen option's label or
 * the AsyncSelectOrInput free-text fallback the schema also allows.
 *
 * @param values The validated form values.
 * @returns The JSON payload the board-certification endpoints expect.
 * @throws If neither a certificate nor its free-text fallback is filled in.
 */
export const transformFormValuesToApi = (values: BoardCertFormValues): string => {
  // [MCV-1627] The certificate fields, like Specialty Board and Specialty, may
  // carry only the AsyncSelectOrInput free-text fallback. Resolve both shapes
  // up front so this guard agrees with the schema -- checking only the select
  // value would throw on a form the schema had just accepted.
  //
  // Read only the pair belonging to the selected type. Switching type unmounts
  // one certificate field and mounts the other, and react-hook-form keeps the
  // unmounted one's value, so a record edited from SUBSPECIALTY to SPECIALTY
  // still held its old subspecialty certificate -- which, being first in the
  // `||` chain below, outranked the specialty certificate the user had just
  // entered. BoardCertForm clears these on every ancestor and type change;
  // gating here as well means no retained value can cross types even if a
  // future field is added to that cascade without one.
  const isSubspecialty = values.certType.value === 'SUBSPECIALTY';
  const subspecialtyName = isSubspecialty
    ? values.subSpecialtyCertificate?.label || values._subSpecialtyName || ''
    : '';
  const specialtyCertName = isSubspecialty
    ? ''
    : values.specialtyCertificate?.label || values._specialtyCertName || '';
  if (!subspecialtyName && !specialtyCertName) {
    throw new Error('Sub Certificate or Certificate must be filled out');
  }

  // The schema (superRefine) accepts either a selected option OR the free-text
  // fallback (_specialtyBoardName / _specialtyName). Preserve that contract in
  // the API payload by falling back to the free-text values when no option is
  // selected — otherwise a valid form would ship empty strings to the server.
  const transformedCert: CreatedBoardCertification = {
    activeFlag: isActive(values.expiryDate, values.status.label),
    boardName: values.medicalBoard?.label || values._medicalBoardName || '',
    // [MCV-1627] `certName` is the CERTIFICATE's name: the schema's closing
    // refine assigns it from the certificate fields, and the grid's
    // "Certification" column renders it. This serialized `specialty.label`
    // instead, so saving a SPECIALTY record replaced the selected certificate
    // ("General Internal Medicine") with the specialty it belongs to
    // ("Internal Medicine"). The specialty is already carried by
    // `specialtyName` below, so nothing needed it here.
    certName: subspecialtyName || specialtyCertName || '',
    certNum: values.certNum || '',
    certType: values.certType.value,
    expiryDate: values.expiryDate || undefined,
    medicalBoardId: values.medicalBoard?.id ? parseInt(values.medicalBoard?.id) : undefined,
    mocReqFlag: values.mocReqFlag ?? false,
    notes: values.notes || '',
    origCertDate: values.origCertDate,
    recertDate: values.recertDate || undefined,
    specialtyBoardName: values.specialtyBoard?.label || values._specialtyBoardName || '',
    specialtyBoardId: values.specialtyBoard?.id ? parseInt(values.specialtyBoard?.id) : undefined,
    // Gated on type for the same reason as the names above: a retained id from
    // the other branch would otherwise point the record at a certificate that
    // does not belong to its type.
    specialtyCertificationId:
      !isSubspecialty && values.specialtyCertificate?.id
        ? parseInt(values.specialtyCertificate.id)
        : undefined,
    specialtyId: values.specialty?.id ? parseInt(values.specialty?.id) : undefined,
    specialtyName: values.specialty?.label || values._specialtyName || '',
    status: values.status.label as BoardCertificationStatuses,
    subspecialtyName,
    subspecialtyCertificationId:
      isSubspecialty && values.subSpecialtyCertificate?.id
        ? parseInt(values.subSpecialtyCertificate.id)
        : undefined,
    warnings: values.warnings || {},
    validFlag: values.validFlag || true,
  };
  return JSON.stringify(transformedCert);
};

/**
 * Hydrates the board-certification form from a stored (or AI-parsed) record.
 *
 * Each `_`-prefixed field is the free-text fallback for one select and mirrors
 * that select's own source, so a value entered as text reappears as text on
 * edit. Fields an AI-parsed record may leave null are coerced to safe defaults
 * for the user to correct.
 *
 * @param response The serialized BoardCertification record.
 * @returns Form values ready for react-hook-form's defaultValues.
 */
export const transformBoardCertToFormValues = (response: string): BoardCertFormValues => {
  const cert: BoardCertification = JSON.parse(response);

  // AI extraction explicitly permits null for every string below. The edit
  // form is the human-review-and-correction mechanism, so normalize that API
  // contract at the boundary instead of leaking null into string-only form
  // controls (and ultimately Zod's implementation-oriented type messages).
  const certType = cert.certType ?? '';
  const certName = cert.certName ?? '';
  const certNum = cert.certNum ?? '';
  const origCertDate = cert.origCertDate ?? '';
  const specialtyName = cert.specialtyName ?? '';
  const parsedSubspecialtyName = cert.subspecialtyName ?? '';
  const boardName = cert.boardName ?? '';
  const specialtyBoardName = cert.specialtyBoardName ?? '';
  const recertDate = cert.recertDate ?? '';
  const expiryDate = cert.expiryDate ?? '';
  const status = cert.status ?? '';
  const notes = cert.notes ?? '';

  // [MCV-1627] AI-parsed SUBSPECIALTY records populate `certName` but leave the
  // normalized `subspecialtyName` empty. Hydrating `subSpecialtyCertificate`
  // from `subspecialtyName` alone therefore left it undefined while the sibling
  // `_subSpecialtyName` text input below showed the parsed name -- so the user
  // saw a filled-in field that the schema's certificate check still counted as
  // empty, trapping them on step 1 until they re-typed it (AsyncSelectOrInput
  // mirrors typed text into the select field on change, which is why "move on
  // without editing" was the failing path). Mirror the SPECIALTY branch, which
  // has always fallen back to `certName`.
  const subspecialtyName = parsedSubspecialtyName || (certType === 'SUBSPECIALTY' ? certName : '');

  /**
   * Because of the functionality of having a textInput/select and ZOD, we can not have those two fields be the same name
   * without it barking. Hence the _fields. They should only be used to populate TextInput fields on edit.
   */
  return {
    // [MCV-1627] Each `_` field is the free-text fallback for ONE select, so it
    // has to mirror that select's own source. `_specialtyName` read `certName`,
    // which is the certificate -- harmless only while `certName` was wrongly
    // being written from the specialty. `_specialtyCertName` was never
    // hydrated at all, so reopening a record whose Specialty Certificate fell
    // back to a text input showed it blank.
    _specialtyName: specialtyName,
    _specialtyCertName: certType === 'SPECIALTY' ? certName : '',
    _subSpecialtyName: subspecialtyName,
    _specialtyBoardName: specialtyBoardName,
    _medicalBoardName: boardName,
    _certName: certName,
    _tblBoardName: getBoardAbbr(cert),
    certName,
    // Unknown required values must stay unknown. Choosing the first real option
    // here silently invented business facts when an AI-parsed record was saved.
    certType: certType ? { label: certType, value: certType } : { label: '', value: '' },
    certNum,
    expiryDate,
    id: cert.id,
    origCertDate,
    medicalBoard: boardName
      ? {
          label: boardName,
          value: boardName,
          id: cert.medicalBoardId?.toString(),
        }
      : undefined,
    specialtyBoard: specialtyBoardName
      ? {
          label: specialtyBoardName,
          value: specialtyBoardName,
          id: cert.specialtyBoardId?.toString(),
        }
      : undefined,
    specialty: specialtyName
      ? {
          label: specialtyName,
          value: specialtyName,
          id: cert.specialtyId?.toString(),
        }
      : undefined,
    specialtyCertificate:
      certType === 'SPECIALTY' && certName
        ? {
            label: certName,
            value: certName,
            id: cert.specialtyCertificationId?.toString(),
          }
        : undefined,
    subSpecialtyCertificate: subspecialtyName
      ? {
          label: subspecialtyName,
          value: subspecialtyName,
          id: cert.subspecialtyCertificationId?.toString(),
        }
      : undefined,
    status: status ? { label: status, value: status } : { label: '', value: '' },
    mocReqFlag: cert.mocReqFlag ?? false,
    notes,
    recertDate,
  };
};

export const transformBoardCertsToFormValues = (response: string): BoardCertFormValues[] => {
  const certs: BoardCertification[] = JSON.parse(response);
  return certs.map((cert) => transformBoardCertToFormValues(JSON.stringify(cert)));
};

/**
 * Determines if Certificate is active or not depending on dates and status
 * Since expiredDate is optional, default to false if undefined,
 * if not undefined, date check with todays date
 * @param expiredDateString
 * @param status
 */
export const isActive = (expiredDateString: string | undefined, status: string): boolean => {
  const todaysDate = new Date().getTime();
  const expiredDate = expiredDateString ? new Date(expiredDateString).getTime() : undefined;
  if (INACTIVE_STATUSES.includes(status)) {
    return false;
  }
  if (ACTIVE_STATUSES.includes(status)) {
    return true;
  }
  if (!expiredDate) return false;
  if (todaysDate < expiredDate) {
    return true;
  } else {
    return false;
  }
};

export const getBoardCertActiveStatus = (
  expiredDateString: string | undefined,
  status: string
): 'Active' | 'Inactive' | 'Unknown' => {
  if (ACTIVE_STATUSES.includes(status)) {
    return 'Active';
  }
  if (INACTIVE_STATUSES.includes(status)) {
    return 'Inactive';
  }
  if (!expiredDateString) {
    return 'Unknown';
  }
  return isActive(expiredDateString, status) ? 'Active' : 'Inactive';
};

/**
 * Grabs Board name abbreviations if possible for table
 * - Tries the Specialty Board code/name first
 * - Falls back to the Medical Board code/name
 * - Finally tries the flat text columns (custom entries)
 * - Returns '--' only when nothing is populated.
 *
 * The nested-object name fallbacks cover certs that were created by picking a
 * master specialty/medical board (so the FK + relationship are populated) but
 * where the `_specialty_board_name` / `_board_name` text columns were left
 * NULL — in which case the flat top-level fields come back empty from the API.
 */
const getBoardAbbr = (cert: BoardCertification): string => {
  if (cert.specialtyBoard?.specialtyBoardCode) return cert.specialtyBoard.specialtyBoardCode;
  if (cert.medicalBoard?.boardCode) return cert.medicalBoard.boardCode;
  if (cert.specialtyBoardName) return cert.specialtyBoardName;
  if (cert.boardName) return cert.boardName;
  if (cert.specialtyBoard?.name) return cert.specialtyBoard.name;
  if (cert.medicalBoard?.boardName) return cert.medicalBoard.boardName;
  return '--';
};
